import { useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import LinearScale from 'linear-scale'
import { clamp } from '../../../../utils/math'
import Surface from './surface'
import Viewport from './viewport'
import Handle from './handle'
import { ThemeContext } from '../../../../base/ThemeContext'

const Scrubber = props => {
  const colorScheme = useContext(ThemeContext)
  const prevPositionRef = useRef(null)

  const handleViewportPanStart = hammerEvent => {
    hammerEvent.preventDefault()

    const { timeStamp, center } = hammerEvent
    prevPositionRef.current = center

    props.onViewportPanStart()
  }

  const handleViewportPanMove = hammerEvent => {
    hammerEvent.preventDefault()

    const { center, timeStamp } = hammerEvent

    const prevCenter = prevPositionRef.current
    prevPositionRef.current = center
    if (prevCenter) {
      // Note: Assigning to variables as one of the objects is sometimes null and we would like to know which one (in production)
      const prevCenterX = prevCenter.x
      const currentCenterX = center.x

      const movementX = (prevCenterX - currentCenterX) * -1
      const movementY = 0

      moveViewport(movementX, movementY)
    }
  }

  const handleViewportPanEnd = hammerEvent => {
    hammerEvent.preventDefault()

    prevPositionRef.current = null
    props.onViewportPanEnd()
  }

  const moveViewport = (movementX, movementY) => {
    const { surfaceWidth, viewportWidth, viewportX } = props

    const minX = 0
    const maxX = surfaceWidth - viewportWidth
    const nextViewportX = clamp(viewportX + movementX, minX, maxX)

    const newViewportLeft = Math.abs(nextViewportX)
    const newViewportRight = newViewportLeft + viewportWidth

    const scale = LinearScale([0, surfaceWidth], [0, 1])
    const nextLeft = scale(newViewportLeft)
    const nextRight = scale(newViewportRight)

    props.onViewportPanMove(nextLeft, nextRight)
  }

  const handleHandlePanStart = (hammerEvent, handleIdentifier) => {
    const { center, timeStamp } = hammerEvent
    prevPositionRef.current = center
    props.onViewportPanStart()
  }

  const handleHandlePanMove = (hammerEvent, handleIdentifier) => {
    hammerEvent.preventDefault()
    const { center, timeStamp } = hammerEvent
    const prevCenter = prevPositionRef.current
    prevPositionRef.current = center

    if (prevCenter) {
      // Note: Assigning to variables as one of the objects is sometimes null and we would like to know which one (in production)
      const prevCenterX = prevCenter.x
      const currentCenterX = center.x

      const movementX = (prevCenterX - currentCenterX) * -1
      const movementY = 0

      moveHandle(movementX, movementY, handleIdentifier)
    }
  }

  const handleHandlePanEnd = (hammerEvent, handleIdentifier) => {
    prevPositionRef.current = null
    props.onViewportPanEnd()
  }

  const moveHandle = (movementX, movementY, handleIdentifier) => {
    const { maxWidthRatio, minWidthRatio, viewportX, surfaceWidth, viewportWidth } = props
    let minX, maxX, nextHandleX, newViewportLeft, newViewportRight, nextLeft, nextRight

    const minViewportWidth = surfaceWidth * maxWidthRatio
    const maxViewportWidth = surfaceWidth * minWidthRatio

    const scale = LinearScale([0, surfaceWidth], [0, 1])

    // calculate the current left and right, then use that, combined with the min/max size and bounds to calcualte min left and right
    switch (handleIdentifier) {
      case 'left':
        const currentRightX = viewportX + viewportWidth
        minX = clamp(currentRightX - maxViewportWidth, 0, surfaceWidth)
        maxX = clamp(currentRightX - minViewportWidth, 0, surfaceWidth)
        nextHandleX = clamp(viewportX + movementX, minX, maxX)

        newViewportRight = viewportX + viewportWidth
        newViewportLeft = Math.abs(nextHandleX)

        nextLeft = scale(newViewportLeft)
        nextRight = scale(newViewportRight)
        break

      case 'right':
        const currentLeftX = viewportX
        minX = clamp(currentLeftX + minViewportWidth, 0, surfaceWidth)
        maxX = clamp(currentLeftX + maxViewportWidth, 0, surfaceWidth)
        nextHandleX = clamp(viewportX + viewportWidth + movementX, minX, maxX)

        newViewportRight = Math.abs(nextHandleX)
        newViewportLeft = viewportX

        nextLeft = scale(newViewportLeft)
        nextRight = scale(newViewportRight)
        break
    }
    props.onViewportPanMove(nextLeft, nextRight)
  }

  const {
    x,
    y,
    width,
    height,
    cursors,
    surfaceWidth,
    surfaceHeight,
    viewportX,
    viewportY,
    viewportWidth,
    viewportHeight,
    handleWidth,
    handleHeight,
  } = props
  const scrubberStyle = {
    left: x,
    top: y,
    width: width,
    height: height,
    zIndex: 1,
  }

  const middleY = viewportHeight / 2

  const leftHandleX = viewportX
  const rightHandleX = viewportX + viewportWidth
  return (
    <div className="scrubber" style={scrubberStyle}>
      <Surface
        x={0}
        y={0}
        width={surfaceWidth}
        height={surfaceHeight}
        colorScheme={colorScheme}
        cursor={cursors.surface}
      />
      <Viewport
        x={viewportX}
        y={viewportY}
        width={viewportWidth}
        height={viewportHeight}
        colorScheme={colorScheme}
        cursor={cursors.viewport}
        onPanStart={handleViewportPanStart}
        onPanMove={handleViewportPanMove}
        onPanEnd={handleViewportPanEnd}
      />
      <Handle
        x={leftHandleX}
        y={middleY}
        width={handleWidth}
        height={handleHeight}
        handle={'left'}
        colorScheme={colorScheme}
        cursor={cursors.leftHandle}
        onPanStart={handleHandlePanStart}
        onPanMove={handleHandlePanMove}
        onPanEnd={handleHandlePanEnd}
      />
      <Handle
        x={rightHandleX}
        y={middleY}
        width={handleWidth}
        height={handleHeight}
        handle={'right'}
        colorScheme={colorScheme}
        cursor={cursors.rightHandle}
        onPanStart={handleHandlePanStart}
        onPanMove={handleHandlePanMove}
        onPanEnd={handleHandlePanEnd}
      />
    </div>
  )
}
Scrubber.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  cursors: PropTypes.shape({
    viewport: PropTypes.string.isRequired,
    surface: PropTypes.string.isRequired,
    leftHandle: PropTypes.string.isRequired,
    rightHandle: PropTypes.string.isRequired,
  }).isRequired,
  // scroller properties
  maxWidthRatio: PropTypes.number.isRequired,
  minWidthRatio: PropTypes.number.isRequired,
  // surface
  surfaceWidth: PropTypes.number.isRequired,
  surfaceHeight: PropTypes.number.isRequired,
  // viewport
  viewportX: PropTypes.number.isRequired,
  viewportY: PropTypes.number.isRequired,
  viewportWidth: PropTypes.number.isRequired,
  viewportHeight: PropTypes.number.isRequired,
  // general styling and sizing proerties
  handleWidth: PropTypes.number.isRequired,
  handleHeight: PropTypes.number.isRequired,

  // handlers
  onViewportPanStart: PropTypes.func.isRequired,
  onViewportPanMove: PropTypes.func.isRequired,
  onViewportPanEnd: PropTypes.func.isRequired,
}
Scrubber.defaultProps = {
  onViewportPanStart: noop,
  onViewportPanMove: noop,
  onViewportPanEnd: noop,
}

export default Scrubber
